import React from "react";

export default function PrivacyPolicy1() {
  return (
    <div className="space-y-6 text-sm leading-6 md:text-base mt-20">
      <h1 className="text-3xl md:text-4xl font-bold text-purple-700 mb-6">
        Privacy Policy
      </h1>
      {/* Header Info */}
      
      <p>
        At SoloConnect, we are committed to protecting your privacy and ensuring
        a transparent experience. This Privacy Policy explains how we collect,
        use, store, and share your personal information.
      </p>

      {/* Section 1 */}
      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          1. Information We Collect
        </h2>

        <p className="mt-2 font-medium">When you register:</p>
        <ul className="list-disc list-inside ml-4">
          <li>Full name (optional)</li>
          <li>Username</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Password (or social login credentials)</li>
        </ul>

        <p className="mt-2 font-medium">Optional Profile Information:</p>
        <ul className="list-disc list-inside ml-4">
          <li>Age</li>
          <li>Gender</li>
          <li>Interests</li>
          <li>Travel preferences</li>
          <li>Profile photo</li>
        </ul>

        <p className="mt-2 font-medium">Automatically Collected:</p>
        <ul className="list-disc list-inside ml-4">
          <li>Travel routes and dates</li>
          <li>Payment logs (via third-party)</li>
          <li>App usage data (e.g., time spent, messages sent)</li>
        </ul>
      </section>

      {/* Section 2 */}
      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          2. How We Use Your Information
        </h2>
        <p className="mt-2">We use your data to:</p>
        <ul className="list-disc list-inside ml-4">
          <li>Create and manage your account</li>
          <li>Suggest travel matches</li>
          <li>Enable and monitor chats</li>
          <li>Improve platform functionality</li>
          <li>Process payments and send confirmations</li>
          <li>Respond to support queries</li>
          <li>Prevent abuse and enforce our policies</li>
        </ul>
      </section>

      {/* Section 3 */}
      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          3. Who We Share Data With
        </h2>
        <p className="mt-2">We do not sell or rent your personal data.</p>
        <p>We may share limited data with:</p>
        <ul className="list-disc list-inside ml-4">
          <li>
            <strong>Payment Processors:</strong> Stripe, Razorpay, PhonePe
          </li>
          <li>
            <strong>Cloud Hosting & Analytics Providers:</strong> Firebase, AWS,
            Google Analytics
          </li>
          <li>
            <strong>Law Enforcement or Authorities:</strong> When required by
            law or to protect safety
          </li>
        </ul>
      </section>

      <h4 className="text-lg font-semibold mb-2">4. Your Profile Visibility</h4>
      <ul className="list-disc ml-6 mb-6 text-sm leading-relaxed">
        <li>
          Your <strong>real name and private details</strong> are never shown
          publicly unless you choose to share them.
        </li>
        <li>
          Other users can view your <strong>username, profile photo, bio, and interests</strong>.
        </li>
      </ul>

      <h4 className="text-lg font-semibold mb-2">5. Data Security</h4>
      <p className="mb-2 text-sm">
        We use industry-standard security practices including:
      </p>
      <ul className="list-disc ml-6 mb-6 text-sm leading-relaxed">
        <li>SSL encryption</li>
        <li>Secure server hosting</li>
        <li>Tokenized payment gateways</li>
      </ul>
      <p className="mb-6 text-sm">
        However, no system is 100% immune. Users are encouraged to use strong
        passwords and never share sensitive information through chat.
      </p>

      <h4 className="text-lg font-semibold mb-2">6. Data Retention</h4>
      <p className="mb-2 text-sm">
        We retain your data as long as your account is active or as required for:
      </p>
      <ul className="list-disc ml-6 mb-6 text-sm leading-relaxed">
        <li>Compliance with legal obligations</li>
        <li>Fraud prevention</li>
        <li>Resolving disputes</li>
      </ul>
      <p className="mb-6 text-sm">
        If you delete your account, data will be anonymized or securely deleted
        within 30 days.
      </p>

      <h4 className="text-lg font-semibold mb-2">7. Your Rights</h4>
      <p className="mb-2 text-sm">As a user, you have the right to:</p>
      <ul className="list-disc ml-6 mb-6 text-sm leading-relaxed">
        <li><strong>Access</strong> the personal data we store about you</li>
        <li><strong>Update or correct</strong> inaccurate or outdated information</li>
        <li><strong>Request deletion</strong> of your account and associated data</li>
        <li><strong>Withdraw consent</strong> for features like location sharing or marketing emails</li>
      </ul>
      <p className="mb-6 text-sm">
        Requests can be made via: <a href="mailto:support@soloconnect.com" className="text-blue-600">support@soloconnect.com</a>
      </p>

      <h4 className="text-lg font-semibold mb-2">8. Children’s Privacy</h4>
      <p className="mb-6 text-sm">
        SoloConnect is intended for users 18 and older. We do not knowingly
        collect data from minors. If we learn that someone under 18 has
        registered, we will delete the account promptly.
      </p>

      <h4 className="text-lg font-semibold mb-2">9. Cookies and Tracking</h4>
      <p className="mb-2 text-sm">We use cookies and device identifiers to:</p>
      <ul className="list-disc ml-6 mb-6 text-sm leading-relaxed">
        <li>Analyze app usage</li>
        <li>Improve user experience</li> 
        <li>Serve relevant content</li>
      </ul>
      <p className="mb-6 text-sm">
        You may control cookies in your device or browser settings.
      </p>

      <h4 className="text-lg font-semibold mb-2">10. Third-Party Links and Services</h4>
      <p className="mb-6 text-sm">
        We may include links to third-party websites or services. SoloConnect is
        not responsible for the privacy practices or content of those external services.
      </p>

      <h4 className="text-lg font-semibold mb-2">11. Policy Changes</h4>
      <p className="mb-6 text-sm">
        We may update this Privacy Policy periodically. Major changes will be
        communicated through the app or email. Please review regularly to stay informed.
      </p>

      <h4 className="text-lg font-semibold mb-2">12. Contact Us</h4>
      <p className="text-sm mb-2">For privacy concerns or questions, contact:</p>
      <a href="mailto:support@soloconnect.com" className="text-blue-600 text-sm">
        support@soloconnect.in
      </a>
       <div className="space-y-1">
        <p>
          <strong>Effective Date:</strong> 10th July 2025
        </p>
        <p>
          <strong>App Name:</strong> SoloConnect
        </p>
        <p>
          <strong>Last Updated:</strong> 10th July 2025
        </p>
      </div>
    </div>
  );
}
